<script setup>
/* Transferhistorie im Reiter Transfer Info (SectorTransfer.vue), zwischen Vereinsfeld
 * und Vertragsfeldern.
 *
 * Den Reiter gibt es nur für Spieler mit Status "transferred" und seit 2026-10-04
 * für alle Rollen (Bereich "players" in domain/playerTabs.js), damit auch Gäste die
 * Historie sehen. Wer Zeilen lesen darf, regelt die Datenbank (player_transfers);
 * hier steht nur, was die Oberfläche daraus macht.
 *
 * Anzeige im Transfermarkt-Stil. Jede Zeile ist ein Kasten und ein Raster mit
 * benannten Bereichen (kind, date, route, note, actions) ohne feste Pixelbreiten:
 * Mobil sind es einspaltige Karten, der Aufbau lässt sich später am Desktop zur
 * Tabelle umstellen, indem nur grid-template-areas geändert werden.
 * Oben links die Art des Wechsels, oben rechts das Datum, darunter die Route
 * (abgebender → aufnehmender Verein, beide mit Logo), darunter Notiz und rechts
 * unten die gedämpften Textknöpfe Bearbeiten/Löschen.
 *
 * Es gibt keine Gebührenanzeige, auch nicht als leeres Feld, und keine AFH-Markierung.
 *
 * Bearbeiten und Löschen sehen nur Rollen mit can('players','write') — das
 * entspricht der Datenbankregel (admin, board, data). Für alle anderen ist der
 * Abschnitt ohne Einträge ausgeblendet — außer bei transferierten Spielern
 * (Prop showEmpty, Entscheidung Bastian 2026-10-07): dort steht statt einer leeren
 * Fläche der ruhige Hinweis "No transfers recorded yet." ohne Knöpfe.
 *
 * Löschen entfernt nur die Zeile; Logo-Dateien bleiben im Speicher (ein Logo
 * kann von anderen Zeilen oder vom aktuellen Verein genutzt werden).
 *
 * Nach dem Speichern oder Löschen erscheint kurz eine Rückmeldung neben der
 * Überschrift (wie "Saved." im Panel), denn der Dialog schließt sich dabei. */
import { ref, computed, defineAsyncComponent, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuth } from '../composables/useAuth.js';
import { useConfirm } from '../composables/useConfirm.js';
import { useTransfers } from '../composables/useTransfers.js';
import { fmtDate } from '../domain/format.js';
import {
  sortTransfers, transferLabel, transferFromCurrentClub,
} from '../domain/transfers.js';
import ClubLogo from './ClubLogo.vue';
import { useFootballClubs } from '../composables/useFootballClubs.js';
import { transferClub } from '../domain/footballClub.js';

/* Der Dialog wird erst geladen, wenn er gebraucht wird */
const TransferDialog = defineAsyncComponent(() => import('./TransferDialog.vue'));

const props = defineProps({
  player: { type: Object, required: true },
  showEmpty: { type: Boolean, default: false },
});

const { t } = useI18n();
const { can } = useAuth();
const { confirm } = useConfirm();
const { items, busy, error, add, update, remove } = useTransfers(() => props.player.id);

const { clubs, byId: clubById } = useFootballClubs();
const canEdit = computed(() => can('players', 'write'));
const rows = computed(() => sortTransfers(items.value));

/* Ein noch nicht gespeicherter Spieler hat keine Kennung, an die sich Zeilen hängen ließen */
const persisted = computed(() => !!props.player.id && props.player._isNew !== true);
const visible = computed(() => persisted.value && (canEdit.value || props.showEmpty || rows.value.length > 0));

const canUseCurrentClub = computed(
  () => !!(props.player.currentClubId || props.player.currentClub || props.player.currentClubLogoPath)
);

/* ---------- Rückmeldung ---------- */
const notice = ref(null); // 'saved' | 'deleted'
let noticeTimer = null;
function flash(key) {
  notice.value = key;
  clearTimeout(noticeTimer);
  noticeTimer = setTimeout(() => { notice.value = null; }, 3500);
}
onBeforeUnmount(() => clearTimeout(noticeTimer));

/* ---------- Dialog ---------- */
const dialogOpen = ref(false);
const editing = ref(null);
const prefill = ref(null);

function openAdd(withCurrentClub = false) {
  editing.value = null;
  prefill.value = withCurrentClub ? transferFromCurrentClub(props.player, clubs.value) : null;
  error.value = null;
  dialogOpen.value = true;
}
function openEdit(row) {
  editing.value = row;
  prefill.value = null;
  error.value = null;
  dialogOpen.value = true;
}
function closeDialog() {
  dialogOpen.value = false;
  editing.value = null;
  prefill.value = null;
  error.value = null;
}

async function onSave(payload) {
  const ok = editing.value ? await update(editing.value.id, payload) : await add(payload);
  if (ok) {
    closeDialog();
    flash('saved');
  }
}

async function onDelete(row) {
  const accepted = await confirm({
    title: t('transferHistory.deleteTitle'),
    message: t('transferHistory.deleteMessage'),
  });
  if (!accepted) return;
  if (await remove(row.id)) flash('deleted');
}

function kindText(row) {
  return t(transferLabel(row.kind).key);
}

/* Eine Seite der Zeile: Verein aus dem Register (Verweis), sonst der alte Text */
function side(row, which) {
  return transferClub(clubById.value, row, which);
}
/* Sie ist nur da, wenn es Name oder Logo gibt */
function hasSide(club) {
  return !!((club.name && club.name.trim()) || club.logoPath);
}
</script>

<template>
  <!-- "doc-group" gibt der Überschrift dieselbe Gestaltung wie in den anderen Reitern
       (main.css: .doc-group h3). Kein eigener Hintergrund, kein eigener Rahmen. -->
  <section v-if="visible" class="th doc-group" data-testid="transfer-history">
    <header class="th-head">
      <h3>{{ $t('transferHistory.title') }}</h3>
      <span v-if="notice" class="th-msg" role="status">{{ $t(`transferHistory.${notice}`) }}</span>
      <button v-if="canEdit && rows.length" type="button" class="btn ghost small" @click="openAdd(false)">
        {{ $t('transferHistory.add') }}
      </button>
    </header>

    <p v-if="error && !dialogOpen" class="th-error">{{ $t(`errors.${error}`) }}</p>

    <!-- Leerzustand: Hinweis für alle (nur bei showEmpty), Hinweistext und Knöpfe nur für Editoren -->
    <div v-if="!rows.length" class="th-empty">
      <p class="th-empty-title">{{ $t('transferHistory.empty') }}</p>
      <p v-if="canEdit" class="th-empty-hint">{{ $t('transferHistory.emptyHint') }}</p>
      <div v-if="canEdit" class="th-empty-actions">
        <button type="button" class="btn small" @click="openAdd(false)">
          {{ $t('transferHistory.add') }}
        </button>
        <button v-if="canUseCurrentClub" type="button" class="btn ghost small" @click="openAdd(true)">
          {{ $t('transferHistory.useCurrentClub') }}
        </button>
      </div>
    </div>

    <ul v-else class="th-list">
      <li v-for="row in rows" :key="row.id" class="th-row">
        <span class="th-kind">{{ kindText(row) }}</span>
        <span class="th-date">{{ fmtDate(row.date) }}</span>

        <div class="th-route">
          <span v-if="hasSide(side(row, 'from'))" class="th-club">
            <ClubLogo :path="side(row, 'from').logoPath" :size="28" />
            <span v-if="side(row, 'from').name" class="th-club-name">{{ side(row, 'from').name }}</span>
          </span>
          <span
            v-if="hasSide(side(row, 'from')) && hasSide(side(row, 'to'))"
            class="th-arrow"
            aria-hidden="true"
          >→</span>
          <span v-if="hasSide(side(row, 'to'))" class="th-club">
            <ClubLogo :path="side(row, 'to').logoPath" :size="28" />
            <span v-if="side(row, 'to').name" class="th-club-name">{{ side(row, 'to').name }}</span>
          </span>
        </div>

        <p v-if="row.note" class="th-note">{{ row.note }}</p>

        <div v-if="canEdit" class="th-actions">
          <button
            type="button"
            class="th-act"
            :disabled="busy"
            :aria-label="$t('transferHistory.dialogEdit')"
            @click="openEdit(row)"
          >
            {{ $t('common.edit') }}
          </button>
          <button
            type="button"
            class="th-act danger"
            :disabled="busy"
            :aria-label="$t('transferHistory.delete')"
            @click="onDelete(row)"
          >
            {{ $t('common.delete') }}
          </button>
        </div>
      </li>
    </ul>

    <TransferDialog
      v-if="dialogOpen"
      :player="player"
      :transfer="editing"
      :prefill="prefill"
      :saving="busy"
      :error="error"
      @close="closeDialog"
      @save="onSave"
    />
  </section>
</template>

<style scoped>
.th {
  margin-top: 18px;
  margin-bottom: 4px;
}
/* Die Überschrift behält Schrift und Farbe von ".doc-group h3"; Abstand und Linie liegen
   hier an der Kopfzeile, damit der "Add transfer"-Knopf rechts daneben stehen kann. */
.th-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--border-soft);
}
.th-head h3 {
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}
.th-msg {
  font-size: var(--fs-xs);
  font-weight: 600;
  color: var(--green);
}
.th-error {
  margin: 0 0 10px;
  font-size: var(--fs-xs);
  color: var(--red);
}

.th-empty {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.th-empty-title {
  margin: 0;
  font-size: var(--fs-sm);
  color: var(--muted);
}
.th-empty-hint {
  margin: 0;
  font-size: var(--fs-xs);
  color: var(--dim);
}
.th-empty-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
}

.th-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Benannte Bereiche: für eine spätere Tabelle am Desktop nur diese Zeile umstellen */
.th-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  grid-template-areas:
    'kind date'
    'route route'
    'note actions';
  align-items: center;
  gap: 6px 12px;
  padding: 10px 12px;
  background: var(--bg);
  border: 1px solid var(--border-soft);
  border-radius: 10px;
}
.th-kind {
  grid-area: kind;
  font-size: var(--fs-sm);
  font-weight: 600;
  color: var(--text);
}
.th-date {
  grid-area: date;
  font-size: var(--fs-xs);
  font-weight: 600;
  color: var(--green);
  text-align: right;
}
.th-route {
  grid-area: route;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 0;
}
.th-club {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.th-club-name {
  font-size: var(--fs-md);
  color: var(--text);
  overflow-wrap: anywhere;
}
.th-arrow {
  color: var(--dim);
}
.th-note {
  grid-area: note;
  align-self: end;
  margin: 0;
  font-size: var(--fs-xs);
  color: var(--dim);
  overflow-wrap: anywhere;
}
/* Dezent: kleine, gedämpfte Textknöpfe rechts unten im Kasten */
.th-actions {
  grid-area: actions;
  align-self: end;
  justify-self: end;
  display: flex;
  gap: 4px;
}
.th-act {
  padding: 4px 6px;
  border: 0;
  background: transparent;
  color: var(--dim);
  font: inherit;
  font-size: var(--fs-2xs);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.th-act:hover {
  color: var(--muted);
}
.th-act.danger:hover {
  color: var(--red);
}
.th-act:disabled {
  opacity: 0.5;
  cursor: default;
}
</style>
